import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import { Plugins } from "@capacitor/core";
import { CLAIM_ADMIN, CLAIM_RUNNER, CLAIM_VENDOR } from "@/constants/claims";
import PlatformService from "@/services/Platform";

const state = {
  user: null,
  userToken: null,
  userClaims: {},
  authError: null,
  skipSignUp: false
};

const getters = {
  user({ user }) {
    return user;
  },

  skipSignUp({ skipSignUp }) {
    return skipSignUp;
  },

  authError({ authError }) {
    return authError;
  },

  userToken({ userToken }) {
    return userToken;
  },

  userIsAdmin({ userClaims }) {
    return userClaims[CLAIM_ADMIN] || false;
  },

  userIsRunner({ userClaims }) {
    return userClaims[CLAIM_RUNNER] || false;
  },

  userIsVendor({ userClaims }) {
    return userClaims[CLAIM_VENDOR] || false;
  },

  userSignInWithEmail({ user }) {
    return (
      user.providerData[0].providerId ===
      firebase.auth.EmailAuthProvider.PROVIDER_ID
    );
  }
};

const socialAuth = ({ context, provider, nativeAuthProvider }) => {
  if (PlatformService.isWeb()) {
    return webAuthProvider(provider, context);
  } else {
    return nativeAuthProvider(context);
  }
};

const handleAuthState = (auth, commit) => {
  return auth
    .then(({ user }) => {
      commit("setUser", { user });
      return true;
    })
    .catch(error => {
      commit("setAuthError", { error });
      return false;
    });
};

const webAuthProvider = (provider, { commit }) => {
  return handleAuthState(firebase.auth().signInWithPopup(provider), commit);
};

const googleAuth = context => {
  return socialAuth({
    context,
    provider: new firebase.auth.GoogleAuthProvider(),
    nativeAuthProvider: context => {
      const { GoogleAuth } = Plugins;
      return GoogleAuth.signIn().then(res => {
        return nativeAuthGoogle(res.authentication.idToken, context);
      });
    }
  });
};

const nativeAuthGoogle = (idToken, { commit }) => {
  var provider = new firebase.auth.GoogleAuthProvider();
  var credential = provider.credential({
    idToken
  });
  return handleAuthState(
    firebase.auth().signInWithCredential(credential),
    commit
  );
};

const appleAuth = context => {
  return socialAuth({
    context,
    provider: new firebase.auth.OAuthProvider("apple.com"),
    nativeAuthProvider: context => {
      const { SignInWithApple } = Plugins;
      return SignInWithApple.Authorize().then(res => {
        return nativeAuthApple(res.response.identityToken, context);
      });
    }
  });
};

const nativeAuthApple = (idToken, { commit }) => {
  var provider = new firebase.auth.OAuthProvider("apple.com");
  var credential = provider.credential({
    idToken
  });
  return handleAuthState(
    firebase.auth().signInWithCredential(credential),
    commit
  );
};

const facebookAuth = context => {
  return socialAuth({
    context,
    provider: new firebase.auth.FacebookAuthProvider(),
    nativeAuthProvider: context => {
      const { FacebookLogin } = Plugins;
      const permissions = ["email"];
      return FacebookLogin.login({ permissions }).then(res => {
        return nativeAuthFacebook(res.accessToken.token, context);
      });
    }
  });
};

const nativeAuthFacebook = (accessToken, { commit }) => {
  var provider = new firebase.auth.FacebookAuthProvider();
  var credential = provider.credential({ accessToken });
  return handleAuthState(
    firebase.auth().signInWithCredential(credential),
    commit
  );
};

const actions = {
  /**
   * This happens when the app is first loaded, right after firebase auth finishes a callback
   */
  initializeUser({ commit, dispatch }) {
    commit("setUserFromFirebase");

    // this isn't great and is pretty coupled
    dispatch("order/initializeOpenOrderWatcher", null, { root: true });
  },

  /**
   * Called during sign in email+password screen
   */
  async signInWithEmail({ commit, dispatch }, { email, password }) {
    commit("clearAuthError");

    return firebase
      .auth()
      .signInWithEmailAndPassword(email, password)
      .then(() => {
        commit("setUserFromFirebase");

        // this isn't great and is pretty coupled
        dispatch("order/initializeOpenOrderWatcher", null, { root: true });

        return true;
      })
      .catch(error => {
        commit("setAuthError", { error });
        return false;
      });
  },

  /**
   * Clear firebase, then ourselves
   */
  signOut({ commit, dispatch }) {
    return firebase
      .auth()
      .signOut()
      .then(() => {
        commit("setUser", { user: null });
        commit("setUserClaims", { userClaims: {} });
        commit("setUserToken", { userToken: null });
        dispatch("order/clearAllOrderData", null, { root: true });
        dispatch("processor/clearProcessorLocation", null, { root: true });
      });
  },

  signUpWithEmail({ commit }, { email, password }) {
    commit("clearAuthError");

    return firebase
      .auth()
      .createUserWithEmailAndPassword(email, password)
      .then(({ user }) => {
        return user
          .updateProfile({
            photoURL: `https://ui-avatars.com/api/?name=SS&size=200`
          })
          .then(() => {
            return user;
          });
      })
      .then(user => {
        commit("setUser", { user });
        return true;
      })
      .catch(error => {
        commit("setAuthError", { error });
        return false;
      });
  },

  signUpWithGoogle(context) {
    return googleAuth(context);
  },

  signInWithGoogle(context) {
    return googleAuth(context);
  },

  signInWithFacebook(context) {
    return facebookAuth(context);
  },

  signUpWithFacebook(context) {
    return facebookAuth(context);
  },

  signInWithApple(context) {
    return appleAuth(context);
  },

  signUpWithApple(context) {
    return appleAuth(context);
  },

  signUpSkip({ commit }) {
    commit("setSkipSignUp");
  },

  updatePassword({ commit }, { oldPassword, newPassword }) {
    commit("clearAuthError");

    const user = firebase.auth().currentUser;
    const credential = firebase.auth.EmailAuthProvider.credential(
      user.email,
      oldPassword
    );

    return user
      .reauthenticateWithCredential(credential)
      .then(() => {
        return user.updatePassword(newPassword).then(() => {
          return true;
        });
      })
      .catch(error => {
        commit("setAuthError", { error });
        return false;
      });
  },

  updateProfile({ commit }, parameters) {
    commit("clearAuthError");

    const user = firebase.auth().currentUser;

    // we update our own profile here because of the vuex state. Double work is happening
    // unfortunately as when the db update happens, the cloud function will try to apply
    // the already applied changes to the auth record. It doesn't hurt anything.  The reason
    // for the cloud function, though, is for when users are edited by someone besides themselves
    return user
      .updateProfile(parameters)
      .then(() => {
        commit("setUser", { user });
        return true;
      })
      .then(() => {
        parameters.updated = firebase.firestore.FieldValue.serverTimestamp();

        return firebase
          .firestore()
          .collection("users")
          .doc(user.uid)
          .update(parameters);
      })
      .catch(error => {
        commit("setAuthError", { error });
        return false;
      });
  },

  updateEmail({ commit }, { email }) {
    commit("clearAuthError");

    const user = firebase.auth().currentUser;

    return user
      .updateEmail(email)
      .then(() => {
        let updated = firebase.firestore.FieldValue.serverTimestamp();

        return firebase
          .firestore()
          .collection("users")
          .doc(user.uid)
          .update({ email, updated });
      })
      .catch(error => {
        commit("setAuthError", { error });
        return false;
      });
  },

  /**
   * This is called after updateEmail fails maybe with needing to be authenticated recently again
   */
  authThenUpdateEmail({ commit, dispatch }, { password, email }) {
    commit("clearAuthError");

    const user = firebase.auth().currentUser;
    const credential = firebase.auth.EmailAuthProvider.credential(
      user.email,
      password
    );

    return user
      .reauthenticateWithCredential(credential)
      .then(() => {
        return dispatch("updateEmail", { email });
      })
      .catch(error => {
        commit("setAuthError", { error });
        return false;
      });
  }
};

const mutations = {
  /**
   * Set our state based on the firebase
   */
  async setUserFromFirebase(state) {
    state.user = firebase.auth().currentUser;
    if (state.user) {
      const idTokenResult = await firebase
        .auth()
        .currentUser.getIdTokenResult();
      state.userToken = idTokenResult.token;
      state.userClaims = idTokenResult.claims;
    }
  },

  /**
   * Set the User
   */
  setUser(state, { user }) {
    state.user = user;
  },

  setUserClaims(state, { userClaims }) {
    state.userClaims = userClaims;
  },

  setUserToken(state, { userToken }) {
    state.userToken = userToken;
  },

  /**
   * when we have auth errors
   */
  setAuthError(state, { error }) {
    state.authError = error;
  },

  setSkipSignUp(state) {
    state.skipSignUp = true;
  },

  clearAuthError(state) {
    state.authError = null;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};
